import { supabase } from '../lib/supabase'

// Shed service to handle all shed-related database operations
export class ShedService {
  constructor() {
    this.tableName = 'sheds_inventory'
    this.useSampleData = false
    // Bind methods so they keep `this` when passed as callbacks (e.g. .map())
    this.transformShedData = this.transformShedData.bind(this)
    this._str = this._str.bind(this)
    this._arr = this._arr.bind(this)
    this._num = this._num.bind(this)
  }

  // Get all sheds (including sold - for admin use)
  async getAllSheds() {
    try {
      if (this.useSampleData) {
        return { data: [], error: null }
      }
      
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('ShedService Error fetching from', this.tableName, ':', error)
        return { data: [], error }
      }

      const transformedData = data?.map(this.transformShedData) || []
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('ShedService Exception:', err)
      return { data: [], error: err }
    }
  }
  
  // Get available sheds only (excludes sold - for public display)
  async getAvailableSheds() {
    try {
      if (this.useSampleData) {
        return { data: [], error: null }
      }
      
      const { data: rawData, error } = await supabase
        .from(this.tableName)
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('ShedService Error fetching available sheds:', error)
        return { data: [], error }
      }
      
      const data = (rawData || []).filter(s => {
        const status = (s.status || '').toLowerCase();
        return !status.includes('sold');
      });

      const transformedData = data?.map(this.transformShedData) || []
      await this.resolveImageUrls(transformedData)
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('ShedService Exception:', err)
      return { data: [], error: err }
    }
  }

  // Get shed by ID
  async getShedById(id) {
    try {
      console.log('ShedService: Fetching shed by ID:', id)
      
      if (this.useSampleData) {
        return { data: null, error: 'Sample data disabled' }
      }
      
      const lookupId = (typeof id === 'string' && id.includes('-')) ? id : parseInt(id)
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', lookupId)
        .single()

      if (error) {
        console.error('ShedService Error:', error)
        return { data: null, error: 'Shed not found' }
      }

      console.log('ShedService: Fetched shed:', data)
      const transformedData = data ? this.transformShedData(data) : null
      if (transformedData) await this.resolveImageUrls(transformedData)
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('ShedService Exception:', err)
      return { data: null, error: 'Shed not found' }
    }
  }

  // Get sheds filtered by type (barn, gable, lean-to, etc.)
  async getShedsByType(types = []) {
    try {
      if (this.useSampleData) {
        return { data: [], error: null }
      }

      let query = supabase
        .from(this.tableName)
        .select('*')

      if (Array.isArray(types) && types.length > 0) {
        query = query.in('shed_type', types)
      }

      const { data, error } = await query
      if (error) {
        console.error('ShedService getShedsByType Error:', error)
        return { data: [], error }
      }

      const transformed = data?.map(this.transformShedData) || []
      return { data: transformed, error: null }
    } catch (err) {
      console.error('ShedService getShedsByType Exception:', err)
      return { data: [], error: err }
    }
  }
  
  // Get available sheds filtered by type (excludes sold)
  async getAvailableShedsByType(types = []) {
    try {
      if (this.useSampleData) {
        return { data: [], error: null }
      }

      let query = supabase
        .from(this.tableName)
        .select('*')

      if (Array.isArray(types) && types.length > 0) {
        query = query.in('shed_type', types)
      }

      const { data: rawData, error } = await query
      
      const data = (rawData || []).filter(s => {
        const status = (s.status || '').toLowerCase();
        return !status.includes('sold');
      });

      if (error) {
        console.error('ShedService getAvailableShedsByType Error:', error)
        return { data: [], error }
      }

      const transformed = data?.map(this.transformShedData) || []
      return { data: transformed, error: null }
    } catch (err) {
      console.error('ShedService getAvailableShedsByType Exception:', err)
      return { data: [], error: err }
    }
  }

  // Get a single random available shed
  async getRandomShed(types = []) {
    const { data, error } = await this.getAvailableShedsByType(types)
    if (error) return { data: null, error }
    if (!data || data.length === 0) return { data: null, error: null }
    const idx = Math.floor(Math.random() * data.length)
    return { data: data[idx], error: null }
  }

  // Re-sign any broken /object/public/ URLs to signed URLs
  async resolveImageUrls(sheds) {
    if (!sheds) return sheds
    const list = Array.isArray(sheds) ? sheds : [sheds]
    const needsFix = list.some(s => s.images?.some(u => typeof u === 'string' && u.includes('/object/public/')))
    if (!needsFix) return sheds

    for (const shed of list) {
      if (!shed.images?.length) continue
      const fixed = []
      for (const url of shed.images) {
        if (typeof url === 'string' && url.includes('/object/public/')) {
          const match = url.match(/\/object\/public\/([^/]+)\/(.+)$/)
          if (match) {
            try {
              const { data, error } = await supabase.storage
                .from(match[1])
                .createSignedUrl(match[2], 365 * 24 * 60 * 60)
              fixed.push(!error && data?.signedUrl ? data.signedUrl : url)
            } catch { fixed.push(url) }
          } else { fixed.push(url) }
        } else { fixed.push(url) }
      }
      shed.images = fixed
    }
    return sheds
  }

  // Transform database format to frontend format
  transformShedData(dbShed) {
    if (!dbShed) return null
    
    let images = this._arr(dbShed.images);
    let videos = this._arr(dbShed.videos);
    let features = this._arr(dbShed.features);
    
    return {
      id: dbShed.id,
      name: this._str(dbShed.name) || `${this._str(dbShed.brand) || ''} ${this._str(dbShed.model) || ''}`.trim() || 'Shed',
      brand: this._str(dbShed.brand),
      dealerName: this._str(dbShed.dealer_name || dbShed.dealerName),
      dealerLocation: this._str(dbShed.dealer_location || dbShed.dealerLocation),
      model: this._str(dbShed.model),
      shedType: this._str(dbShed.shed_type || dbShed.shedType),
      size: this._str(dbShed.size),
      width: dbShed.width,
      depth: dbShed.depth,
      height: dbShed.height,
      squareFeet: dbShed.square_feet || dbShed.squareFeet,
      price: dbShed.price,
      salePrice: dbShed.sale_price || dbShed.salePrice,
      depositAmount: dbShed.deposit_amount || dbShed.depositAmount,
      onSale: dbShed.on_sale || dbShed.onSale,
      salePercentage: dbShed.sale_percentage || dbShed.salePercentage,
      material: this._str(dbShed.material),
      roofStyle: this._str(dbShed.roof_style || dbShed.roofStyle),
      roofMaterial: this._str(dbShed.roof_material || dbShed.roofMaterial),
      sidingType: this._str(dbShed.siding_type || dbShed.sidingType),
      bodyColor: this._str(dbShed.body_color || dbShed.bodyColor),
      trimColor: this._str(dbShed.trim_color || dbShed.trimColor),
      roofColor: this._str(dbShed.roof_color || dbShed.roofColor),
      floorType: this._str(dbShed.floor_type || dbShed.floorType),
      foundation: this._str(dbShed.foundation),
      doorType: this._str(dbShed.door_type || dbShed.doorType),
      windowType: this._str(dbShed.window_type || dbShed.windowType),
      hasRamp: dbShed.has_ramp || dbShed.hasRamp,
      hasShelving: dbShed.has_shelving || dbShed.hasShelving,
      inventoryType: this._str(dbShed.inventory_type || dbShed.inventoryType) || 'physical',
      condition: this._str(dbShed.condition),
      status: this._str(dbShed.status) || 'available',
      serialNumber: this._str(dbShed.serial_number || dbShed.serialNumber),
      stockNumber: this._str(dbShed.stock_number || dbShed.stockNumber),
      yearBuilt: dbShed.year_built || dbShed.yearBuilt,
      warranty: this._str(dbShed.warranty),
      warrantyYears: dbShed.warranty_years || dbShed.warrantyYears,
      loft: dbShed.loft,
      workbench: dbShed.workbench,
      electrical: dbShed.electrical,
      insulation: dbShed.insulation,
      deliveryIncluded: dbShed.delivery_included || dbShed.deliveryIncluded,
      setupIncluded: dbShed.setup_included || dbShed.setupIncluded,
      description: this._str(dbShed.description),
      features: features,
      images: images,
      videos: videos,
      lotLocation: this._str(dbShed.lot_location || dbShed.lotLocation) || '',
      dealer_id: dbShed.dealer_id || null,
      createdAt: dbShed.created_at || dbShed.createdAt
    }
  }

  // Create new shed
  async createShed(shedData) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .insert([this.transformToDbFormat(shedData)])
        .select()
        .single()

      if (error) {
        console.error('ShedService Create Error:', error)
        return { data: null, error }
      }

      return { data: this.transformShedData(data), error: null }
    } catch (err) {
      console.error('ShedService Create Exception:', err)
      return { data: null, error: err }
    }
  }

  // Update shed
  async updateShed(id, shedData) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .update(this.transformToDbFormat(shedData))
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('ShedService Update Error:', error)
        return { data: null, error }
      }

      return { data: this.transformShedData(data), error: null }
    } catch (err) {
      console.error('ShedService Update Exception:', err)
      return { data: null, error: err }
    }
  }

  // Update shed status
  async updateShedStatus(id, newStatus) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .update({ 
          status: newStatus,
          status_updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('ShedService Update Status Error:', error)
        return { data: null, error }
      }

      return { data: this.transformShedData(data), error: null }
    } catch (err) {
      console.error('ShedService Update Status Exception:', err)
      return { data: null, error: err }
    }
  }

  // Delete shed
  async deleteShed(id) {
    try {
      if (this.useSampleData) {
        return { error: 'Sample data mode - no deletion' }
      }

      // Clean up related records (ignore errors for tables that may not exist)
      try {
        await supabase.from('deals').delete().eq('shed_id', id)
      } catch (e) {
        // deals table may not exist yet
      }

      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq('id', id)

      if (error) {
        console.error('ShedService Delete Error:', error)
        return { error }
      }

      return { error: null }
    } catch (err) {
      console.error('ShedService Delete Exception:', err)
      return { error: err }
    }
  }

  // ==========================================
  // MULTI-TENANT METHODS
  // ==========================================

  // Get sheds by dealer ID (for dealer admin)
  async getShedsByDealer(dealerId) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('dealer_id', dealerId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('ShedService getShedsByDealer Error:', error)
        return { data: [], error }
      }

      const transformedData = data?.map(this.transformShedData) || []
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('ShedService getShedsByDealer Exception:', err)
      return { data: [], error: err }
    }
  }

  // Get published sheds by city (for public display)
  async getPublishedShedsByCity(cityId) {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('city_id', cityId)
        .eq('is_published', true)
        .eq('status', 'available')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('ShedService getPublishedShedsByCity Error:', error)
        return { data: [], error }
      }

      const transformedData = data?.map(this.transformShedData) || []
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('ShedService getPublishedShedsByCity Exception:', err)
      return { data: [], error: err }
    }
  }

  // Get all published sheds (for public display when no city selected)
  async getPublishedSheds() {
    try {
      if (this.useSampleData) {
        return { data: [], error: null }
      }

      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('is_published', true)
        .eq('status', 'available')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('ShedService getPublishedSheds Error:', error)
        return { data: [], error }
      }

      const transformedData = data?.map(this.transformShedData) || []
      return { data: transformedData, error: null }
    } catch (err) {
      console.error('ShedService getPublishedSheds Exception:', err)
      return { data: [], error: err }
    }
  }

  // Publish/unpublish a shed
  async togglePublish(id, isPublished) {
    try {
      const updates = {
        is_published: isPublished,
        published_at: isPublished ? new Date().toISOString() : null
      }

      const { data, error } = await supabase
        .from(this.tableName)
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('ShedService togglePublish Error:', error)
        return { data: null, error }
      }

      return { data: this.transformShedData(data), error: null }
    } catch (err) {
      console.error('ShedService togglePublish Exception:', err)
      return { data: null, error: err }
    }
  }

  // Create shed with dealer assignment
  async createShedForDealer(shedData, dealerId, cityId) {
    try {
      const dbData = {
        ...this.transformToDbFormat(shedData),
        dealer_id: dealerId,
        city_id: cityId,
        is_published: false
      }

      const { data, error } = await supabase
        .from(this.tableName)
        .insert([dbData])
        .select()
        .single()

      if (error) {
        console.error('ShedService createShedForDealer Error:', error)
        return { data: null, error }
      }

      return { data: this.transformShedData(data), error: null }
    } catch (err) {
      console.error('ShedService createShedForDealer Exception:', err)
      return { data: null, error: err }
    }
  }

  // Bulk create sheds (for CSV import)
  async bulkCreateSheds(shedsArray, dealerId, cityId) {
    try {
      const dbRows = shedsArray.map(shed => ({
        ...this.transformToDbFormat(shed),
        dealer_id: dealerId || null,
        city_id: cityId || null,
        is_published: false
      }));

      const { data, error } = await supabase
        .from(this.tableName)
        .insert(dbRows)
        .select();

      if (error) {
        console.error('ShedService bulkCreateSheds Error:', error);
        return { data: null, error, inserted: 0 };
      }

      return { data: data?.map(this.transformShedData) || [], error: null, inserted: data?.length || 0 };
    } catch (err) {
      console.error('ShedService bulkCreateSheds Exception:', err);
      return { data: null, error: err, inserted: 0 };
    }
  }

  // Coerce empty strings to null (Postgres rejects "" for numeric/boolean columns)
  _num(val) {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    return isNaN(n) ? null : n;
  }

  // Safely coerce any value to a plain string (prevents React "Objects are not valid as a React child" crash)
  _str(val) {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string') return val;
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);
    // Objects/arrays — try to extract a meaningful string
    if (typeof val === 'object') {
      if (val.text) return String(val.text);
      if (val.value) return String(val.value);
      if (val.name) return String(val.name);
      try { return JSON.stringify(val); } catch { return ''; }
    }
    return String(val);
  }

  // Safely coerce to array
  _arr(val) {
    if (Array.isArray(val)) return val;
    if (!val) return [];
    if (typeof val === 'string') {
      try { const parsed = JSON.parse(val); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
    }
    return [];
  }

  // Transform frontend format to database format
  transformToDbFormat(shed) {
    return {
      name: shed.name || null,
      brand: shed.brand || null,
      model: shed.model || null,
      shed_type: shed.shedType || null,
      size: shed.size || null,
      width: this._num(shed.width),
      depth: this._num(shed.depth),
      height: this._num(shed.height),
      square_feet: this._num(shed.squareFeet),
      price: this._num(shed.price),
      sale_price: this._num(shed.salePrice),
      deposit_amount: this._num(shed.depositAmount),
      on_sale: shed.onSale || false,
      sale_percentage: this._num(shed.salePercentage) || 0,
      material: shed.material || null,
      roof_style: shed.roofStyle || null,
      roof_material: shed.roofMaterial || null,
      siding_type: shed.sidingType || null,
      body_color: shed.bodyColor || null,
      trim_color: shed.trimColor || null,
      roof_color: shed.roofColor || null,
      floor_type: shed.floorType || null,
      foundation: shed.foundation || null,
      door_type: shed.doorType || null,
      window_type: shed.windowType || null,
      has_ramp: shed.hasRamp || false,
      has_shelving: shed.hasShelving || false,
      inventory_type: shed.inventoryType || 'physical',
      condition: shed.condition || null,
      status: shed.status || 'available',
      serial_number: shed.serialNumber || null,
      stock_number: shed.stockNumber || null,
      year_built: this._num(shed.yearBuilt),
      warranty: shed.warranty || null,
      warranty_years: this._num(shed.warrantyYears),
      loft: shed.loft || false,
      workbench: shed.workbench || false,
      electrical: shed.electrical || false,
      insulation: shed.insulation || false,
      delivery_included: shed.deliveryIncluded || false,
      setup_included: shed.setupIncluded || false,
      description: shed.description || null,
      features: shed.features || [],
      images: shed.images || [],
      videos: shed.videos || [],
      lot_location: shed.lotLocation || null,
      dealer_id: shed.dealer_id || null
    }
  }
}

// Export singleton instance
export const shedService = new ShedService()
